import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { LEASES, STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import {
  buildLeaseFormData,
  buildLeaseRelationParams,
} from './leaseApiHelpers';

const getStatusLabel = (status) =>
  status?.label ?? status?.name ?? status?.title ?? String(status?.id ?? '');

const CreateLease = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [leaseRecord, setLeaseRecord] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('lease'));
      } catch {
        try {
          response = await api.get(STATUSES.BY_MODEL('Lease'));
        } catch {
          response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }

      const list = extractList(response.data);
      const arr = Array.isArray(list) ? list : [];
      setStatusOptions(
        arr.map((status) => ({
          id: status.id,
          label: getStatusLabel(status),
          raw: status,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setLeaseRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(LEASES.BY_ID(editId), {
          params: buildLeaseRelationParams(),
        });
        const record = data?.data ?? data;
        if (!cancelled) setLeaseRecord(record);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load lease');
        navigate('/leases');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !leaseRecord) return;

    setName(leaseRecord.name ?? '');
    setDescription(leaseRecord.description ?? '');

    const nestedStatus =
      leaseRecord.status && typeof leaseRecord.status === 'object'
        ? leaseRecord.status
        : null;
    const statusId = leaseRecord.status_id ?? nestedStatus?.id ?? undefined;

    if (statusId) {
      if (nestedStatus) {
        const option = {
          id: statusId,
          label: getStatusLabel(nestedStatus),
          raw: nestedStatus,
        };
        setSelectedStatus(option);
        setStatusOptions((prev) =>
          prev.some((item) => String(item.id) === String(statusId))
            ? prev
            : [...prev, option]
        );
      } else {
        const option = statusOptions.find(
          (item) => String(item.id) === String(statusId)
        );
        setSelectedStatus(option ?? { id: statusId, label: String(statusId), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
  }, [isEdit, leaseRecord, statusOptions]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedStatus?.id || !name.trim()) {
      toast.warn('Select status and enter a lease name');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      description: description.trim(),
    };
    const formData = buildLeaseFormData(values);
    const query = new URLSearchParams(buildLeaseRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${LEASES.BY_ID(editId)}?${query}`, formData);
        toast.success('Lease updated');
      } else {
        await api.post(LEASES.LIST, formData);
        toast.success('Lease created');
      }
      navigate('/leases');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEdit ? 'Failed to update lease' : 'Failed to create lease')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/leases')} sx={{ mb: 2 }}>
          Back to leases
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit lease' : 'Create lease'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, value) => setSelectedStatus(value)}
                  loading={statusLoading}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Status" required fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Create lease'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/leases')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateLease;
